import { Placeholder, Text } from '@lmn/design-system';
import { IconArrowLeftLinear, IconArrowUp, IconSearch } from '@/app/common/icons/libraryIcons';
import { mutate } from 'swr';
import { eachDayOfInterval, endOfDay, format, isAfter, isPast, getDay, parse } from 'date-fns';
import type { HTMLAttributes, KeyboardEventHandler, MouseEventHandler, ReactNode, SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import styled, { css } from 'styled-components';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { createPriceCalendarClickedPayload } from '@/app/common/starTrack/events/priceCalendarClicked.ts';
import { createPriceCalendarShownPayload } from '@/app/common/starTrack/events/priceCalendarShown.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { getTotalPerNightsLabel } from '@/app/components/search/components/Card/utils/getPriceDetails';
import { useInitialParams } from './InitialParamsProvider';
import { useOffersContext } from './OffersProvider';
import { sendMetricIncrement } from './metrics';
import { useCuriosity } from './CuriosityProvider';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import type { DateRange } from './common/dates';
import {
  addDaysWithFormat,
  changeDateFormat,
  formatDate,
  getDateRange,
  getTripDatesPriceCalendar,
  PRICE_CALENDAR_API_DATE_FORMAT,
  PRICE_CALENDAR_RANGE_DAYS_DESKTOP,
  PRICE_CALENDAR_RANGE_DAYS_MOBILE,
  PRICE_CALENDAR_RANGE_DAYS_TABLET,
  SERVICES_DATES_FORMAT,
} from './common/dates';
import getSearchParams from '../search/common/getSearchParams';
import customCss from './css/customCss';
import { useTranslation } from '../translations/Translation';
import { useIsDesktopOrGreater, useIsMobile, useIsTabletOrGreater } from './hooks/useMediaSSR';
import { getPriceCalendarKey, usePriceCalendar } from './hooks/usePriceCalendar';

const BAR_HEIGHT_MAX_PRICE = 102;
const BAR_HEIGHT_MIN_PRICE = 38;

const Nights = styled.span`
  color: ${(p) => p.theme.commons.colors.gray400};
  font-size: ${(p) => p.theme.commons.font.sizes.xsmall};
  line-height: ${(p) => p.theme.commons.font.heights.xsmall};
  padding-bottom: 4px;
`;

const Price = styled.div`
  position: relative;
  ${(p) => p.theme.mixins.font('Title6')};
`;

const cssHighlightedPriceBar = css`
  background-color: ${(p) => p.theme.commons.colors.primary};
  color: white;
  ${Nights} {
    color: white;
  }
`;

const LoadingBar = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 30px;
  bottom: 4px;
`;

const TooltipContainer = styled.div`
  z-index: 1;
  position: absolute;
  display: none;
  transform: translateY(-100%);
  flex-direction: column;
  align-items: center;
  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid white;
  }
`;

const StyledDiv = styled.div``;
const AnimatedDiv = animated(StyledDiv);

const PriceBarFillContainer = styled(AnimatedDiv)<{
  $hasData: boolean;
  $pastDate: boolean;
  $hasPrice: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 4px 4px 0 0;
  background-color: ${(p) => (p.$hasData ? p.theme.commons.colors.gray100 : 'white')};
  ${(p) => (p.$pastDate ? 'pointer-events: none;' : '')}

  ${Price} {
    ${(p) => (p.$hasPrice ? 'padding-top: 4px;' : '')}
  }
`;

const PriceBarDate = styled.div<{ $pastDate: boolean; $weekend: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: ${(p) => p.theme.commons.font.sizes.xsmall};
  font-weight: ${(p) => (p.$weekend ? p.theme.commons.font.weights.bold : p.theme.commons.font.weights.regular)};
  line-height: ${(p) => p.theme.commons.font.heights.xsmall};
  ${(p) => (p.$pastDate ? `color: ${p.theme.commons.colors.gray200}` : '')}
`;

const PriceBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  row-gap: 4px;
  padding: 0;

  @media ${customCss.breakpoints.sm} {
    padding: 24px 0;
  }

  &.highlighted {
    ${TooltipContainer} {
      display: flex;
    }

    .price-bar {
      ${cssHighlightedPriceBar}
      &:before {
        display: initial;
      }
    }
  }

  &:first-child {
    ${TooltipContainer} {
      transform: translate(40px, -100%);

      &::after {
        transform: translateX(-40px);
      }
    }
  }

  &:last-child {
    ${TooltipContainer} {
      transform: translate(-40px, -100%);

      &::after {
        transform: translateX(40px);
      }
    }
  }
`;

const PriceBars = styled.div`
  display: flex;
  column-gap: 2px;
  height: 100%;
  flex-grow: 1;

  &:hover {
    .highlighted {
      ${TooltipContainer} {
        display: none;
      }
    }

    & > *:hover {
      &:not(:has(.price-bar--past-date)) {
        cursor: pointer;
      }

      ${TooltipContainer} {
        display: flex;
      }

      .price-bar {
        &,
        ${Nights} {
          color: ${(p) => p.theme.commons.colors.primary};
        }

        &--has-data {
          background-color: ${(p) => p.theme.commons.colors.primary50};
        }

        &--highlighted {
          ${cssHighlightedPriceBar}
        }

        &:before {
          display: initial;
        }
      }
    }
  }
`;

export default function PriceCalendar(props: HTMLAttributes<HTMLElement>) {
  const { pageType, productDetailKey } = useInitialParams();
  const { commonData, localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { offersData, resetOffers, isLoadingRooms } = useOffersContext();
  const { locale, currency } = localization;
  const searchParams = getSearchParams();
  const debug = searchParams[URLParameters.DEBUG];
  const {
    nights: tripNights,
    checkin,
    checkout,
    setDates,
    datesOrigin,
    accommodationOnly,
    isTrain,
  } = useUrlSearchParams();
  const tripDates = getTripDatesPriceCalendar(checkin, checkout);
  const [priceCalendarDates, setPriceCalendarDates] = useState<DateRange | undefined>();
  const { data, isLoading } = usePriceCalendar(priceCalendarDates);

  const { starTrack } = useCommonClient();

  const isMobile = useIsMobile(commonData.isMobileDevice);
  const isTablet = useIsTabletOrGreater();
  const isDesktop = useIsDesktopOrGreater();

  const rangeDays = isDesktop
    ? PRICE_CALENDAR_RANGE_DAYS_DESKTOP
    : isTablet
      ? PRICE_CALENDAR_RANGE_DAYS_TABLET
      : isMobile
        ? PRICE_CALENDAR_RANGE_DAYS_MOBILE
        : undefined;

  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const showingCalendarFirstTime = useRef(true);

  const onBarClick = (product: PriceCalendarProduct) => {
    if (!product.isPastDate && !isLoading) {
      if (product.data?.datefrom && product.data?.dateto) {
        selectDate(product.data.datefrom, product.data.dateto, product.data.nights, product?.data?.price);
      } else {
        selectDate(
          product.date,
          addDaysWithFormat(product.date, PRICE_CALENDAR_API_DATE_FORMAT, tripNights),
          tripNights,
          product?.data?.price
        );
      }
    }
  };

  const selectDate = (datefrom: string, dateto: string, _nights: number, price: number | undefined) => {
    if (datefrom === selectedDate) return;

    const dateFromFormatted = changeDateFormat(datefrom, PRICE_CALENDAR_API_DATE_FORMAT, 'yyyyMMdd');
    const dateToFormatted = changeDateFormat(dateto, PRICE_CALENDAR_API_DATE_FORMAT, 'yyyyMMdd');
    setSelectedDate(datefrom);
    resetOffers();
    setDates({
      checkin: dateFromFormatted,
      checkout: dateToFormatted,
      nights: _nights,
      origin: 'PRICE_CALENDAR',
    });
    sendCuriosityClickEvent({
      event_name: 'click_calendar_bar',
      event_context: 'price_calendar',
      event_value: `day_${dateFromFormatted}`,
    });

    starTrack.priceCalendarClicked(
      createPriceCalendarClickedPayload({
        productDetailKey,
        currency,
        datefrom,
        price,
      })
    );
  };

  const previousRange = () => {
    if (!rangeDays) return;
    const minDate = data?.products && data.products.length > 0 ? data?.products[0].date : undefined;
    if (minDate) {
      setPriceCalendarDates({
        ...getDateRange(
          addDaysWithFormat(minDate, PRICE_CALENDAR_API_DATE_FORMAT, -(rangeDays + 1)),
          PRICE_CALENDAR_API_DATE_FORMAT,
          PRICE_CALENDAR_API_DATE_FORMAT,
          rangeDays,
          rangeDays
        ),
        nights: tripNights,
      });
    }
  };

  const nextRange = () => {
    if (!rangeDays) return;
    const maxDate =
      data?.products && data.products.length > 0 ? data?.products[data.products.length - 1].date : undefined;
    if (maxDate) {
      setPriceCalendarDates({
        ...getDateRange(
          addDaysWithFormat(maxDate, PRICE_CALENDAR_API_DATE_FORMAT, rangeDays + 1),
          PRICE_CALENDAR_API_DATE_FORMAT,
          PRICE_CALENDAR_API_DATE_FORMAT,
          rangeDays,
          rangeDays
        ),
        nights: tripNights,
      });
    }
  };

  // Initialize window on mount; re-center when nights or breakpoint (rangeDays) changes.
  useEffect(() => {
    if (!rangeDays || !tripDates?.checkinFormatted) return;
    setPriceCalendarDates({
      ...getDateRange(
        tripDates.checkinFormatted,
        PRICE_CALENDAR_API_DATE_FORMAT,
        PRICE_CALENDAR_API_DATE_FORMAT,
        rangeDays,
        rangeDays
      ),
      nights: tripNights,
    });
  }, [tripNights, rangeDays]);

  // Re-center when the date picker or alternative dates changes the check-in date.
  useEffect(() => {
    if (!rangeDays || (datesOrigin !== 'ALTERNATIVE_DATES' && datesOrigin !== 'DATES_PICKER')) return;
    setPriceCalendarDates({
      ...getDateRange(checkin, SERVICES_DATES_FORMAT, PRICE_CALENDAR_API_DATE_FORMAT, rangeDays, rangeDays),
      nights: tripNights,
    });
  }, [datesOrigin, checkin]);

  // Update the selected-date highlight without resetting the window (e.g. bar click).
  useEffect(() => {
    setSelectedDate(tripDates?.checkinFormatted);
  }, [tripDates?.checkinFormatted]);

  useEffect(() => {
    if (
      (offersData?.cheapestMealPlan?.finalPrice || offersData?.noAvailableRooms) &&
      data &&
      tripDates?.checkinFormatted
    ) {
      const offerFinalPriceToShow = accommodationOnly
        ? offersData.cheapestMealPlan?.finalTotalPrice
        : offersData.cheapestMealPlan?.finalPrice;
      const index = data.products.findIndex((product) => product.date === tripDates.checkinFormatted);
      if (
        index !== -1 &&
        data.products &&
        (!data.products[index].data ||
          data.products[index].data?.price !== offerFinalPriceToShow ||
          offersData?.noAvailableRooms)
      ) {
        const cheapestPriceValue = offerFinalPriceToShow ? Math.floor(offerFinalPriceToShow) : 0;
        let withPrice = 0;
        const updatedProducts: PriceCalendarProduct[] = data.products.map((product) => {
          if (product.data?.price) withPrice += 1;
          if (product.date === tripDates.checkinFormatted) {
            if (!product.data) {
              if (cheapestPriceValue === 0) {
                sendMetricIncrement(`app.pricecalendar.${pageType}.notavailable`);
              } else {
                sendMetricIncrement(`app.pricecalendar.${pageType}.newprice`);
              }
              return {
                ...product,
                data: {
                  price: offersData?.noAvailableRooms ? 0 : cheapestPriceValue,
                  currency,
                  nights: tripNights,
                  datefrom: product.date,
                  dateto: addDaysWithFormat(product.date, PRICE_CALENDAR_API_DATE_FORMAT, tripNights),
                  taxes: 0,
                  age: 0,
                  is_per_person: false,
                },
              };
            }
            if (product.data?.price) {
              sendCuriosityClickEvent({
                event_name: 'event_calendar_price',
                event_context:
                  cheapestPriceValue === product.data?.price
                    ? 'no_change'
                    : cheapestPriceValue > product.data?.price
                      ? 'increment'
                      : 'decrement',
                event_value: `${product.data?.price}`,
                event_value_additional: `${cheapestPriceValue}`,
              });

              if (cheapestPriceValue > product.data?.price) {
                sendMetricIncrement(`app.pricecalendar.${pageType}.priceincrement`);
              } else if (cheapestPriceValue < product.data?.price) {
                sendMetricIncrement(`app.pricecalendar.${pageType}.pricedecrement`);
              }
            }

            return { ...product, data: { ...product.data, price: cheapestPriceValue } };
          }

          return product;
        });

        if (showingCalendarFirstTime.current) {
          showingCalendarFirstTime.current = false;
          sendCuriosityClickEvent({
            event_name: 'event_calendar_days_with_price',
            event_context: 'price_calendar',
            event_value: `${withPrice || 0}`,
          });
        }

        if (priceCalendarDates?.from && priceCalendarDates?.to && tripNights) {
          mutate(
            getPriceCalendarKey(priceCalendarDates?.from, priceCalendarDates?.to, tripNights),
            {
              ...data,
              products: updatedProducts,
              ...(offerFinalPriceToShow
                ? {
                    maxPrice: Math.max(data.maxPrice, offerFinalPriceToShow),
                    minPrice: !data.minPrice ? offerFinalPriceToShow : Math.min(data.minPrice, offerFinalPriceToShow),
                  }
                : undefined),
            },
            false
          );
        }
      }
    }
  }, [offersData?.cheapestMealPlan?.finalPrice, offersData?.noAvailableRooms, data?.time]);

  const maxPriceDifference = data ? data.maxPrice - data.minPrice : 0;
  const minDate = data?.products?.[0]?.date
    ? parse(data.products[0].date, PRICE_CALENDAR_API_DATE_FORMAT, new Date())
    : new Date();

  // Hotel Only calendar prices are stay totals (the selected date is overwritten with finalTotalPrice),
  // so label them like the room list does instead of "pp". (MPH-1201)
  const priceTypeLabel = accommodationOnly
    ? (getTotalPerNightsLabel({ nights: tripNights, t }) ?? '')
    : t(
        isTrain
          ? 'vacation.select.room.price.train.perperson.and.nights.mobile'
          : 'vacation.select.room.price.dp.perperson.and.nights.mobile',
        {
          nights: tripNights.toString(),
        }
      );

  const animationFn = useCallback(springBarAnimation(data?.minPrice || 0, maxPriceDifference), [
    data?.minPrice,
    maxPriceDifference,
  ]);

  const currentData = isLoading ? getDefaultData(priceCalendarDates?.from, priceCalendarDates?.to) : data?.products;

  const transitions = useTransition(currentData, {
    keys: (product) => product?.date || '',
    from: { height: BAR_HEIGHT_MIN_PRICE },
    enter: animationFn,
    update: animationFn,
  });

  return (
    <PriceCalendarContainer {...props} expanded={false}>
      <div
        css={`
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        `}
      >
        {isMobile && (
          <Text
            variant="BodySmall"
            css={`
              padding: 16px 32px;
            `}
          >
            {priceTypeLabel}
          </Text>
        )}
        <div
          css={`
            display: flex;
            flex-direction: row;
            align-items: stretch;
            width: 100%;
            flex-grow: 1;
            height: 172px;

            @media ${customCss.breakpoints.sm} {
              column-gap: 8px;
              padding: 0 24px;
              align-items: center;
              height: 220px;
            }
          `}
        >
          <PaginationButton type="left" onClick={previousRange} disabled={!isAfter(minDate, new Date())} />
          <PriceBars>
            {data
              ? transitions((animatedStyle, product) => {
                  if (!product) return null;
                  const parsedDate = parse(product.date, 'yyyy-MM-dd', new Date());
                  const weekDay = getDay(parsedDate);
                  const highlight = product.date === selectedDate;
                  const notAvailable = product.data && !product.data.price;
                  const showBarLoading = highlight && isLoadingRooms;
                  return (
                    <PriceBarContainer
                      key={product.date}
                      className={`${highlight ? 'highlighted' : ''}`}
                      onClick={() => onBarClick(product)}
                      onKeyDown={(event) => (event.key === 'Enter' ? onBarClick(product) : null)}
                      role="button"
                      tabIndex={0}
                    >
                      <PriceBarFillContainer
                        style={animatedStyle}
                        key={product.date}
                        className={`price-bar${highlight ? ' price-bar--highlighted' : ''}${
                          product.isPastDate ? ' price-bar--past-date' : ''
                        }${product.data !== undefined ? ' price-bar--has-data' : ''}`}
                        $hasData={product.data !== undefined}
                        $hasPrice={!!product.data?.price}
                        $pastDate={product.isPastDate}
                      >
                        {!isMobile && !showBarLoading && !notAvailable && !product.isPastDate ? (
                          <Tooltip
                            price={product.data?.price ? formatPrice({ price: product.data.price, localization }) : ''}
                            priceType={priceTypeLabel || ''}
                          />
                        ) : null}
                        <div
                          css={css`
                            position: relative;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            height: 100%;
                            width: 100%;
                            border-bottom: 1px solid
                              ${
                                highlight
                                  ? css`
                                      ${(p) => p.theme.commons.colors.primary}
                                    `
                                  : css`
                                      ${(p) => p.theme.commons.colors.gray100}
                                    `
                              };
                            justify-content: ${
                              product.data !== undefined && !!product.data?.price ? 'space-between' : 'center'
                            };
                          `}
                        >
                          {product.data ? (
                            <>
                              {!showBarLoading || animatedStyle.height.goal > 42 ? (
                                <Price>
                                  {notAvailable
                                    ? t('vacation.select.details.pricecalendar.date.notavailable')
                                    : formatPrice({ price: product.data.price, localization })}
                                </Price>
                              ) : (
                                <div />
                              )}
                              {product.data.price && !showBarLoading ? (
                                <Nights>
                                  {tripNights === 1
                                    ? `1 ${t('vacation.common.nights')}`
                                    : `${tripNights} ${t('vacation.common.nights')}`}
                                </Nights>
                              ) : null}
                            </>
                          ) : !product.isPastDate && !showBarLoading ? (
                            isLoading ? (
                              <Placeholder width="100%" height={`${BAR_HEIGHT_MIN_PRICE}px`} />
                            ) : (
                              <IconSearch
                                css={`
                                  width: 16px;
                                  height: 16px;
                                `}
                              />
                            )
                          ) : null}
                          {showBarLoading && (
                            <LoadingBar>
                              <Spinner />
                            </LoadingBar>
                          )}
                        </div>
                      </PriceBarFillContainer>

                      <PriceBarDate $pastDate={product.isPastDate} $weekend={weekDay === 0 || weekDay === 6}>
                        <span>{formatDate(locale, parsedDate, { weekday: 'short' })}</span>
                        <span>
                          {formatDate(locale, parsedDate, {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </span>
                        {debug && (
                          <span>
                            {product.data?.age
                              ? product.data.age >= 60
                                ? `${Math.floor(product.data.age / 60)}h`
                                : `${product.data.age}m`
                              : 'n/a'}
                          </span>
                        )}
                      </PriceBarDate>
                    </PriceBarContainer>
                  );
                })
              : null}
          </PriceBars>
          <PaginationButton type="right" onClick={nextRange} />
        </div>
      </div>
    </PriceCalendarContainer>
  );
}

function PriceCalendarContainer(
  props: { expanded: boolean | undefined; children: ReactNode } & HTMLAttributes<HTMLElement>
) {
  const { children, expanded, ...rest } = props;
  const pageWrapperData = usePageWrapperData();
  const { pageType, productDetailKey } = useInitialParams();
  const [isExpanded, setExpanded] = useState(expanded);
  const { sendCuriosityClickEvent } = useCuriosity();
  const { t } = useTranslation();
  const { starTrack } = useCommonClient();

  useEffect(() => {
    if (expanded === undefined) return;
    setExpanded(expanded);
    sendCuriosityClickEvent(
      {
        event_name: 'event_calendar_shown',
        event_value: expanded ? 'expanded' : 'collapsed',
      },
      { onlyOnce: true }
    );

    starTrack.priceCalendarShown(createPriceCalendarShownPayload({ productDetailKey, expanded }));

    sendMetricIncrement(
      `app.request.${pageType}.price-calendar.show-client.${pageWrapperData.commonData.isBot ? 'bot' : 'user'}.${
        expanded ? 'expanded' : 'collapsed'
      }`
    );
  }, [expanded]);

  const onExpandClick: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    sendCuriosityClickEvent({
      event_name: 'click_show_calendar',
      event_context: 'price_calendar',
      event_value: isExpanded ? 'to_collapse_it' : 'to_expand_it',
    });
    setExpanded((prev) => !prev);
  };
  const onExpandClickKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') setExpanded((prev) => !prev);
    return null;
  };

  return (
    <div
      {...rest}
      css={`
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          margin: 0 24px;
        }

        @media ${customCss.breakpoints.lg} {
          margin: 0 24px;
        }

        @media ${customCss.breakpoints.lgPlus} {
          margin: 0 40px;
        }

        @media ${customCss.breakpoints.xl} {
          margin: 0;
        }
      `}
    >
      <div
        css={`
          padding: 16px;
          display: flex;
          column-gap: 16px;
          align-items: center;
          cursor: pointer;

          @media ${customCss.breakpoints.sm} {
            padding: 16px 24px;
          }
        `}
        role="button"
        onClick={onExpandClick}
        onKeyDown={onExpandClickKeyDown}
        tabIndex={0}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-grow: 1;
          `}
        >
          <Text variant="Title5">{t('vacation.select.details.pricecalendar.title')}</Text>
          <Text
            variant="BodySmall"
            css={css`
              color: ${(p) => p.theme.commons.colors.gray400};
            `}
          >
            {t('vacation.select.details.pricecalendar.description')}
          </Text>
        </div>
        <div
          css={`
            display: flex;
            align-items: center;
          `}
        >
          <IconArrowUp
            css={css`
              color: ${(p) => p.theme.commons.colors.gray400};
              height: 1.25rem;
              width: 1.25rem;
              transform: rotate(${isExpanded ? '0deg' : '180deg'});
              transition: transform 0.2s;

              @media ${customCss.breakpoints.sm} {
                height: 1rem;
                width: 1rem;
              }
            `}
          />
        </div>
      </div>
      <div
        css={`
          overflow: hidden;
          height: ${isExpanded ? '240px' : '0'};
          visibility: ${isExpanded ? 'visible' : 'hidden'};
          transition:
            height 0.5s ease-in-out,
            visibility 0s ${isExpanded ? '0s' : '0.5s'};

          ${TooltipContainer} {
            opacity: ${isExpanded ? '1' : '0'};
            z-index: ${isExpanded ? '1' : '-1'};
            transition: opacity ${isExpanded ? '0.5s cubic-bezier(1.0, 0, 1.0, 1.0)' : '0.2s linear'};
          }

          @media ${customCss.breakpoints.sm} {
            height: ${isExpanded ? '220px' : '0'};
          }
        `}
      >
        <div
          css={css`
            margin: 0 24px;
            border-top: 1px solid ${(p) => p.theme.commons.colors.gray50};
          `}
        />
        {children}
      </div>
    </div>
  );
}

function PaginationButton(props: {
  disabled?: boolean;
  type: 'left' | 'right';
  onClick: (event: SyntheticEvent<Element, Event>) => void;
}) {
  const { type, disabled = false, onClick } = props;

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') onClick(event);
    return null;
  };

  return (
    <div
      onClick={disabled ? () => undefined : onClick}
      onKeyDown={disabled ? () => undefined : onKeyDown}
      role="button"
      aria-label={type === 'left' ? 'Previous dates' : 'Next dates'}
      tabIndex={0}
      css={css`
        display: flex;
        padding: 8px;
        align-items: center;
        user-select: none;
        ${
          disabled
            ? css`
                color: ${(p) => p.theme.commons.colors.gray200};
                @media ${customCss.breakpoints.sm} {
                  background-color: ${(p) => p.theme.commons.colors.gray50};
                }
              `
            : css`
                @media ${customCss.breakpoints.sm} {
                  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
                  &:hover {
                    box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
                    cursor: pointer;
                  }
                }
              `
        }
        @media ${customCss.breakpoints.sm} {
          border-radius: 16px;
        }
      `}
    >
      <IconArrowLeftLinear
        css={`
          width: 16px;
          height: 16px;
          ${type === 'left' ? '' : 'transform: rotate(180deg);'}
        `}
      />
    </div>
  );
}

function Tooltip(props: { price: string; priceType: string }) {
  const { price, priceType } = props;
  const { t } = useTranslation();
  return (
    <TooltipContainer>
      <div
        css={css`
          background: white;
          align-items: center;
          white-space: nowrap;
          display: flex;
          flex-direction: column;
          border-radius: 4px;
          padding: 8px 12px;
          box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
          color: ${(p) => p.theme.commons.colors.gray800};
        `}
      >
        {price ? (
          <>
            <Text
              variant="Caption"
              css={css`
                color: ${(p) => p.theme.commons.colors.primary};
                white-space: nowrap;
              `}
            >
              {t('vacation.common.price.from')}{' '}
              <span
                css={css`
                  ${({ theme }) => theme.kallisto.mixins.font('Title5')};
                `}
              >
                {price}
              </span>
            </Text>
            <Text variant="Caption">{priceType}</Text>
          </>
        ) : (
          <Text
            variant="Caption"
            css={css`
              color: ${(p) => p.theme.commons.colors.primary};
            `}
          >
            {t('vacation.select.details.pricecalendar.searchicon')}
          </Text>
        )}
      </div>
    </TooltipContainer>
  );
}

function Spinner() {
  return (
    <div
      css={css`
        display: inline-block;
        position: absolute;

        & div {
          display: block;
          position: absolute;
          left: -8px;
          top: -8px;
          width: 16px;
          height: 16px;
          border: 2px solid white;
          border-radius: 50%;
          animation: spinner-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
          border-color: white transparent transparent transparent;
        }
        & div:nth-child(1) {
          animation-delay: -0.45s;
        }
        & div:nth-child(2) {
          animation-delay: -0.3s;
        }
        & div:nth-child(3) {
          animation-delay: -0.15s;
        }
        @keyframes spinner-ring {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}
    >
      <div />
      <div />
      <div />
      <div />
    </div>
  );
}

const springBarAnimation =
  (minPrice: number, maxPriceDifference: number) => (product: PriceCalendarProduct | undefined) => ({
    height:
      product?.data?.price && maxPriceDifference
        ? Math.round(
            ((product.data.price - minPrice) * BAR_HEIGHT_MAX_PRICE) / maxPriceDifference + BAR_HEIGHT_MIN_PRICE
          )
        : BAR_HEIGHT_MIN_PRICE,
  });

function getDefaultData(from: string | undefined, to: string | undefined): PriceCalendarProduct[] | undefined {
  if (!from || !to) return undefined;

  const datesRangeArray = eachDayOfInterval({
    start: new Date(from),
    end: new Date(to),
  });

  const products: PriceCalendarProduct[] = datesRangeArray.map((date) => {
    const dateFormatted = format(date, 'yyyy-MM-dd');
    const isPastDate = isPast(endOfDay(date));
    return {
      date: dateFormatted,
      isPastDate,
      data: undefined,
    };
  });
  return products;
}
